import React, { useId } from 'react'
import type { LoyaltyCouponLimits } from '../../../../../types/loyaltyCoupon'
import { CouponLimitToggle } from './CouponLimitToggle'
import * as s from './limitsStyles'

// Mirrors `breadstack-loyalty-pro.html` lines 1122-1187. Only `minOrder`,
// `maxDiscount`, `excludeSale`, and `stackable` are wired today — the other
// four toggles in the HTML (excludeGiftCards, stackMember, inStoreOnly,
// firstOrderOnly) are commented out until the coupon backend ships them.

interface Props {
  limits: LoyaltyCouponLimits
  onChange: (next: LoyaltyCouponLimits) => void
}

const parseFloatOrNull = (raw: string): number | null => {
  const trimmed = raw.trim()
  if (!trimmed) return null
  const n = Number.parseFloat(trimmed)
  return Number.isFinite(n) && n >= 0 ? n : null
}

interface ToggleRowProps {
  name: string
  desc: string
  checked: boolean
  onChange: (next: boolean) => void
  last?: boolean
}

const ToggleRow: React.FC<ToggleRowProps> = ({
  name,
  desc,
  checked,
  onChange,
  last,
}) => (
  // Wrapper is a div (not a label) because <CouponLimitToggle> is a
  // <button role="switch"> with its own aria-label — biome's
  // `noLabelWithoutControl` rule only counts native inputs as controls.
  <div style={last ? s.toggleRowLast : s.toggleRow}>
    <span style={s.toggleRowInfo}>
      <span style={s.toggleRowName}>{name}</span>
      <span style={s.toggleRowDesc}>{desc}</span>
    </span>
    <CouponLimitToggle checked={checked} onChange={onChange} ariaLabel={name} />
  </div>
)

export const CouponRestrictionsBlock: React.FC<Props> = ({
  limits,
  onChange,
}) => {
  const idMinOrder = useId()
  const idMaxDiscount = useId()
  return (
    <div style={s.block}>
      <div style={s.blockHead}>
        <div>
          <div style={s.blockTitle}>Order &amp; item restrictions</div>
          <div style={s.blockDesc}>
            When the coupon will <em>not</em> apply
          </div>
        </div>
      </div>
      <div style={s.blockBody}>
        <div style={s.formRow}>
          <div style={s.formGroup}>
            <label style={s.formLabel} htmlFor={idMinOrder}>
              Minimum order subtotal
            </label>
            <input
              id={idMinOrder}
              style={s.formInput}
              type="number"
              min={0}
              step="0.5"
              placeholder="0"
              value={limits.minOrder ?? ''}
              onChange={(e) =>
                onChange({
                  ...limits,
                  minOrder: parseFloatOrNull(e.target.value),
                })
              }
            />
            <div style={s.formHint}>
              Members must spend at least $X for coupon to apply
            </div>
          </div>
          <div style={s.formGroup}>
            <label style={s.formLabel} htmlFor={idMaxDiscount}>
              Maximum discount cap
            </label>
            <input
              id={idMaxDiscount}
              style={s.formInput}
              type="number"
              min={0}
              step="0.5"
              placeholder="No cap"
              value={limits.maxDiscount ?? ''}
              onChange={(e) =>
                onChange({
                  ...limits,
                  maxDiscount: parseFloatOrNull(e.target.value),
                })
              }
            />
            <div style={s.formHint}>
              Cap on % discounts (e.g., 50% off, max $25)
            </div>
          </div>
        </div>
        <div style={s.toggleList}>
          <ToggleRow
            name="Does not apply to sale items"
            desc="Skip products with a sale price"
            checked={limits.excludeSale}
            onChange={(v) => onChange({ ...limits, excludeSale: v })}
          />
          <ToggleRow
            name="Stack with other coupons"
            desc="Allow combining with another discount code"
            checked={limits.stackable}
            onChange={(v) => onChange({ ...limits, stackable: v })}
            last
          />
          {/* TODO: re-enable when backend ships the remaining restriction flags.
          <ToggleRow
            name="Does not apply to gift cards"
            desc="Gift cards stay full-price regardless"
            checked={limits.excludeGiftCards}
            onChange={(v) => onChange({ ...limits, excludeGiftCards: v })}
          />
          <ToggleRow
            name="Stack with Member Pricing"
            desc="Apply on top of the tier discount"
            checked={limits.stackMember}
            onChange={(v) => onChange({ ...limits, stackMember: v })}
          />
          <ToggleRow
            name="In-store only"
            desc="Block from online checkout"
            checked={limits.inStoreOnly}
            onChange={(v) => onChange({ ...limits, inStoreOnly: v })}
          />
          <ToggleRow
            name="First-time customers only"
            desc="Only members on first paid order"
            checked={limits.firstOrderOnly}
            onChange={(v) => onChange({ ...limits, firstOrderOnly: v })}
            last
          />
          */}
        </div>
      </div>
    </div>
  )
}
